(() => {
  if (customElements.get('terraflex-single-product')) return;
  class TerraFlexSingleProduct extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.events = { signal: this.controller.signal };
      try {
        this.config = JSON.parse(this.querySelector('[data-tf-config]').textContent);
        this.variants = this.config.variants;
        this.form = this.querySelector('[data-tf-form]');
        this.button = this.querySelector('[data-tf-checkout]');
        this.buttonLabel = this.querySelector('[data-tf-button-label]');
        this.originalButtonLabel = this.buttonLabel.textContent;
        this.error = this.querySelector('[data-tf-error]');
        this.count = Number(this.querySelector('[name="tf-pair-count"]:checked').value);
        this.formatter = new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency });
        this.fullFormatter = new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency, currencyDisplay: 'symbol' });
        this.initialVariant = this.variants.find(variant => String(variant.id) === String(this.config.initialVariantId)) || this.variants[0];
        if (!this.initialVariant || !this.config.colors.length) throw new Error('Missing product options');
        this.pairs = [...this.querySelectorAll('[data-tf-pair]')].map(fieldset => ({ fieldset, color: fieldset.querySelector('[data-tf-color]'), size: fieldset.querySelector('[data-tf-size]'), image: fieldset.querySelector('[data-tf-pair-image]'), error: fieldset.querySelector('[data-tf-pair-error]') }));
        const params = new URL(window.location.href).searchParams;
        const requestedVariant = this.variants.find(variant => String(variant.id) === params.get('variant'));
        const requestedColor = this.config.colors.find(color => color.toLocaleLowerCase() === (params.get('color') || '').toLocaleLowerCase());
        const colorPosition = params.get('color_index');
        const localizedColor = colorPosition !== null && /^\d+$/.test(colorPosition) ? this.config.colors[Number(colorPosition)] : null;
        const startingColor = requestedVariant?.options[this.config.colorIndex] || requestedColor || localizedColor || this.initialVariant.options[this.config.colorIndex];
        this.pairs.forEach(pair => { pair.color.value = startingColor; });
        if (requestedVariant || this.config.explicitVariantId) {
          const explicitVariant = requestedVariant || this.variants.find(variant => String(variant.id) === String(this.config.explicitVariantId));
          if (explicitVariant) this.pairs[0].size.value = explicitVariant.options[this.config.sizeIndex];
        }
        this.pairs.forEach((pair, index) => {
          pair.color.addEventListener('change', () => this.selectionChanged(index, true), this.events);
          pair.size.addEventListener('change', () => this.selectionChanged(index, false), this.events);
        });
        this.querySelectorAll('[data-tf-swatch]').forEach(swatch => swatch.addEventListener('click', () => {
          this.pairs[0].color.value = swatch.dataset.tfSwatch;
          this.selectionChanged(0, true);
        }, this.events));
        this.querySelectorAll('[name="tf-pair-count"]').forEach(radio => radio.addEventListener('change', () => {
          this.count = Number(radio.value);
          this.updatePairs();
          this.updatePrices();
          this.clearError();
        }, this.events));
        this.querySelector('[data-tf-previous]').addEventListener('click', () => this.showPhoto(this.photoIndex - 1), this.events);
        this.querySelector('[data-tf-next]').addEventListener('click', () => this.showPhoto(this.photoIndex + 1), this.events);
        this.querySelector('[data-tf-thumbnails]').addEventListener('click', event => {
          const target = event.target.closest('[data-tf-thumb]');
          if (target) this.showPhoto(Number(target.dataset.tfThumb));
        }, this.events);
        this.querySelector('[data-tf-thumbnails]').addEventListener('keydown', event => {
          if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
          const target = event.target.closest('[data-tf-thumb]');
          if (!target) return;
          event.preventDefault();
          const index = Number(target.dataset.tfThumb);
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.photos.length - 1 : index + (event.key === 'ArrowRight' ? 1 : -1);
          this.showPhoto(next);
          this.querySelector(`[data-tf-thumb="${this.photoIndex}"]`).focus();
        }, this.events);
        this.dialog = this.querySelector('.tf-single__zoom-dialog');
        this.querySelector('[data-tf-zoom]').addEventListener('click', () => {
          const image = this.querySelector('[data-tf-zoom-image]');
          const photo = this.photos[this.photoIndex];
          image.src = photo.src;
          image.alt = photo.alt;
          image.width = photo.width;
          image.height = photo.height;
          this.dialog.showModal();
          this.querySelector('[data-tf-zoom-close]').focus();
        }, this.events);
        this.querySelector('[data-tf-zoom-close]').addEventListener('click', () => this.dialog.close(), this.events);
        this.dialog.addEventListener('click', event => {
          if (event.target !== this.dialog) return;
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
        }, this.events);
        this.dialog.addEventListener('close', () => this.querySelector('[data-tf-zoom]').focus({ preventScroll: true }), this.events);
        this.form.addEventListener('submit', event => { event.preventDefault(); this.checkout(); }, this.events);
        window.addEventListener('pageshow', () => this.resetButton(), this.events);
        this.updatePairs();
        this.updateGallery(startingColor);
        this.updatePrices();
        this.resetButton();
      } catch (error) {
        const message = this.querySelector('[data-tf-error]');
        if (message) { message.textContent = this.config?.strings.checkoutError || 'Please refresh the page and try again.'; message.hidden = false; }
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      if (this.dialog?.open) this.dialog.close();
    }
    money(value, full = false) { return (full ? this.fullFormatter : this.formatter).format(value / 100); }
    variantFor(pair) { return this.variants.find(variant => variant.options[this.config.colorIndex] === pair.color.value && variant.options[this.config.sizeIndex] === pair.size.value); }
    priceVariantFor(pair) { return this.variantFor(pair) || this.variants.find(variant => variant.options[this.config.colorIndex] === pair.color.value && variant.available) || this.variants.find(variant => variant.options[this.config.colorIndex] === pair.color.value) || this.initialVariant; }
    selectionChanged(index, colorChanged) {
      const pair = this.pairs[index];
      if (colorChanged) this.updateSizeOptions(pair);
      this.updatePairs();
      if (index === 0) {
        if (colorChanged) this.updateGallery(pair.color.value);
        const url = new URL(window.location.href);
        url.searchParams.set('color', pair.color.value);
        url.searchParams.delete('color_index');
        const variant = this.variantFor(pair);
        if (variant) url.searchParams.set('variant', String(variant.id));
        else url.searchParams.delete('variant');
        window.history.replaceState(window.history.state, '', url);
      }
      this.updatePrices();
      this.clearError();
    }
    updateSizeOptions(pair) {
      [...pair.size.options].forEach(option => {
        if (!option.value) return;
        const variant = this.variants.find(item => item.options[this.config.colorIndex] === pair.color.value && item.options[this.config.sizeIndex] === option.value);
        option.disabled = !variant?.available;
      });
      if (pair.size.selectedOptions[0]?.disabled) pair.size.value = '';
    }
    updatePairs() {
      this.pairs.forEach((pair, index) => {
        pair.fieldset.hidden = index >= this.count;
        pair.fieldset.disabled = index >= this.count;
        this.updateSizeOptions(pair);
        const photo = this.config.media[pair.color.value]?.[0];
        if (photo) pair.image.src = photo.thumb;
        const variant = this.variantFor(pair);
        pair.error.hidden = !pair.size.value || Boolean(variant?.available);
      });
    }
    totals(count) {
      const percent = count === 3 ? 20 : count === 2 ? 10 : 0;
      const selected = this.pairs.slice(0, count).map(pair => this.priceVariantFor(pair));
      const subtotal = selected.reduce((sum, variant) => sum + variant.price, 0);
      const savings = Math.floor(subtotal * percent / 100);
      return { subtotal, savings, total: subtotal - savings };
    }
    updatePrices() {
      [1, 2, 3].forEach(count => {
        const totals = this.totals(count);
        this.querySelector(`[data-tf-pack-total="${count}"]`).textContent = this.money(totals.total);
        this.querySelector(`[data-tf-pack-original="${count}"]`).textContent = this.money(totals.subtotal);
      });
      const first = this.priceVariantFor(this.pairs[0]);
      this.querySelector('[data-tf-unit-price]').textContent = this.money(first.price, true);
      const compare = this.querySelector('[data-tf-unit-compare]');
      compare.hidden = !(first.compare_at_price > first.price);
      if (!compare.hidden) compare.textContent = this.money(first.compare_at_price);
      const totals = this.totals(this.count);
      this.querySelector('[data-tf-total]').textContent = this.money(totals.total, true);
      const savings = this.querySelector('[data-tf-savings]');
      savings.hidden = !totals.savings;
      savings.textContent = this.config.strings.save.replace('[amount]', this.money(totals.savings));
    }
    updateGallery(color) {
      this.photos = this.config.media[color] || [];
      if (!this.photos.length) return;
      const container = this.querySelector('[data-tf-thumbnails]');
      container.replaceChildren();
      this.photos.forEach((photo, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.tfThumb = String(index);
        button.setAttribute('aria-label', this.config.strings.photo.replace('[number]', String(index + 1)).replace('[color]', color));
        const image = document.createElement('img');
        image.src = photo.thumb;
        image.alt = '';
        image.width = photo.width;
        image.height = photo.height;
        image.loading = 'lazy';
        button.append(image);
        container.append(button);
      });
      this.querySelectorAll('[data-tf-swatch]').forEach(swatch => swatch.setAttribute('aria-pressed', String(swatch.dataset.tfSwatch === color)));
      this.querySelector('[data-tf-color-name]').textContent = color;
      this.querySelector('[data-tf-gallery-caption]').textContent = this.config.strings.gallery.replace('[color]', color);
      this.showPhoto(0);
      const detail = document.querySelector('.tf-story__detail-image');
      if (detail) {
        const photo = this.photos[0];
        detail.src = photo.src;
        detail.srcset = `${photo.small} 360w, ${photo.medium} 750w, ${photo.src} 1200w`;
        detail.alt = photo.alt;
      }
    }
    showPhoto(index) {
      this.photoIndex = (index + this.photos.length) % this.photos.length;
      const photo = this.photos[this.photoIndex];
      const image = this.querySelector('.tf-single__image');
      image.src = photo.src;
      image.srcset = `${photo.small} 360w, ${photo.medium} 750w, ${photo.src} 1200w`;
      image.alt = photo.alt;
      image.width = photo.width;
      image.height = photo.height;
      this.querySelectorAll('[data-tf-thumb]').forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.tfThumb) === this.photoIndex)));
      this.querySelector('[data-tf-photo-count]').textContent = `${this.photoIndex + 1} / ${this.photos.length}`;
    }
    clearError() { this.error.hidden = true; this.error.textContent = ''; }
    resetButton() { this.button.disabled = false; this.button.removeAttribute('aria-busy'); this.buttonLabel.textContent = this.originalButtonLabel; }
    checkout() {
      if (this.button.disabled) return;
      this.clearError();
      if (!this.form.reportValidity()) return;
      const selected = this.pairs.slice(0, this.count).map(pair => this.variantFor(pair));
      if (selected.some(variant => !variant?.available)) {
        this.error.textContent = this.config.strings.unavailable;
        this.error.hidden = false;
        return;
      }
      const quantities = new Map();
      selected.forEach(variant => quantities.set(String(variant.id), (quantities.get(String(variant.id)) || 0) + 1));
      if ([...quantities.keys()].some(id => !/^\d+$/.test(id))) return;
      const path = this.config.cartUrl.replace(/\/$/, '');
      const items = [...quantities].map(([id, quantity]) => `${id}:${quantity}`).join(',');
      const url = new URL(`${path}/${items}`, window.location.origin);
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      this.buttonLabel.textContent = this.config.strings.opening;
      try { window.location.assign(url.toString()); }
      catch {
        this.error.textContent = this.config.strings.checkoutError;
        this.error.hidden = false;
        this.resetButton();
      }
    }
  }
  customElements.define('terraflex-single-product', TerraFlexSingleProduct);
})();

